import Intercom, { shutdown } from "@intercom/messenger-js-sdk";
import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ErrorModal from "./components/Alerts/ErrorModal";
import SuccessModal from "./components/Alerts/SuccessModal";
import NotificationPrompt from "./components/NotificationPrompt/NotificationPrompt";
import Router from "./routes/Router";
import { clearPersistedData } from "./store/configureStore";
import { setDefaultDateForBalanceChart } from "./store/reducers/accountSlice";
import { deAuthenticate, setIsProfilePopupVisible } from "./store/reducers/authSlice";
import { PURGE_Brand } from "./store/reducers/brandSlice";
import { Modal } from "antd";
import { generateRandomId, getDeviceType } from "./utils/helpers/browserID";
import { startTokenRefresh, stopTokenRefresh } from "./utils/tokenRefreshService";
import { getIntercomTokenRequest } from "./utils/apis/user";

function App() {
  const data = useSelector((state) => state.account.accountList);
  const { userId, isAuthenticated, refreshToken, rememberMe, idToken } = useSelector((state) => state?.auth);
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [intercomToken, setIntercomToken] = useState(null);

  let ee_device_id = localStorage.getItem("ee-device-id");

  if (!ee_device_id) {
    ee_device_id = generateRandomId();
    localStorage.setItem("ee-device-id", ee_device_id);
  }

  axios.defaults.headers.common["X-EE-Device-Id"] = ee_device_id;
  axios.defaults.headers.common["X-Device-Type"] = getDeviceType();

  let ee_session_token = localStorage.getItem("ee-session-token");
  if (ee_session_token) {
    axios.defaults.headers.common["X-SESSION-TOKEN"] = ee_session_token;
  }

  // Start token refresh timer when user is authenticated with Remember Me
  useEffect(() => {
    if (isAuthenticated && rememberMe && refreshToken) {
      startTokenRefresh(dispatch, refreshToken, rememberMe);
    } else {
      stopTokenRefresh();
    }

    // Cleanup on unmount
    return () => {
      stopTokenRefresh();
    };
  }, [isAuthenticated, rememberMe, refreshToken, dispatch]);

  useEffect(() => {
    if (isAuthenticated && idToken) {
      const fetchIntercomToken = async () => {
        try {
          const response = await getIntercomTokenRequest(idToken);
          const token = response?.data?.intercom_user_jwt
          if (token) {
            setIntercomToken(token);
          }
        } catch (error) {
          console.error("Failed to fetch Intercom token:", error);
        }
      };
      fetchIntercomToken();
    } else {
      setIntercomToken(null);
    }
  }, [isAuthenticated, idToken]);

  useEffect(() => {
    const createdAt = Math.floor(Date.now() / 1000);

    let unAuthUser = {
      app_id: "oa4kzzll",
    };

    if (isAuthenticated) {
      if (intercomToken) {
        // Decode the Intercom JWT payload to prevent user_id mismatch errors
        let decoded = null;
        try {
          const base64Url = intercomToken.split(".")[1];
          if (base64Url) {
            const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
            const jsonPayload = decodeURIComponent(
              window.atob(base64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join("")
            );
            decoded = JSON.parse(jsonPayload);
          }
        } catch (e) {
          console.error("Error decoding Intercom JWT:", e);
        }

        let authUser = {
          app_id: "oa4kzzll",
          user_id: decoded?.user_id || userId,
          name: decoded?.name || `${user?.first_name} ${user?.last_name}`,
          email: decoded?.email || user?.email,
          created_at: decoded?.created_at || createdAt,
          intercom_user_jwt: intercomToken,
          session_duration: 86400000
        };
        Intercom(authUser);
      }
    } else {
      shutdown();
      Intercom(unAuthUser);
    }
  }, [isAuthenticated, userId, user, intercomToken]);

  // const idToken = useSelector((state) => state.auth.idToken);

  // const firstAccount = useSelector((state) => state.account.accountList.data[0]);
  // const loginId = activeAccount || firstAccount;
  // useEffect(() => {
  //   dispatch(getAccountMetrics({idToken, loginId}));
  //
  // }, [loginId]);
  useEffect(() => {
    const isProduction = window.location.hostname === "app.equityedge.io";
    if (!window.location.pathname.includes("program-objectives")) {
      dispatch(setDefaultDateForBalanceChart({start_date: "", end_date: ""}));
    }

    if (isProduction) {
      console.log = () => {};
    }
  }, [window.location.pathname]);

  const selectedBrand = useSelector((state) => state.brand.selectedBrand);

  useEffect(() => {
    document.body.classList.remove("equity_edge", "futures_edge", "crypto_edge");
    document.body.classList.add(selectedBrand.brand); // Use `.brand`
  }, [selectedBrand.brand]); // Depend on `selectedBrand.brand`

  return data ? (
    <>
      <ToastContainer />
      {/* <NotificationPrompt /> */}
      <Router />
      <SuccessModal />
      <ErrorModal />
      <ModalDetails dispatch={dispatch} />
    </>
  ) : null;
}

export default App;
const ModalDetails = ({dispatch}) => {
  const {isProfileComplete} = useSelector((state) => state.auth);

  // Determine the message based on missing fields
  let message = "";
  const missingContact = isProfileComplete?.contact;
  const missingCountry = isProfileComplete?.country;

  if (missingContact && missingCountry) {
    message = "Kindly add your contact and country info to continue using the platform.";
  } else if (missingContact) {
    message = "Kindly add your contact info to continue using the platform.";
  } else if (missingCountry) {
    message = "Kindly add your country info to continue using the platform.";
  }

  return (
    <Modal
      open={isProfileComplete?.isVisible}
      footer={null}
      centered
      closable={false}
      // bodyStyle={{backgroundColor: "#102717"}}
      className="profile-modal"
    >
      <div style={{textAlign: "center", padding: "20px"}}>
        <h2 style={{color: "#fff"}}>Please complete your profile details</h2>
        {/* Only show the message if some info is missing */}
        {message && <p style={{color: "#fff", marginBottom: "20px"}}>{message}</p>}
        <a
          href="/myprofile/general-information"
          onClick={() => dispatch(setIsProfilePopupVisible({isVisible: false}))}
          style={{color: "#010101", textDecoration: "underline", backgroundColor: "#36D66B", border: "none", cursor: "pointer"}}
        >
          Update Profile
        </a>
      </div>
    </Modal>
  );
};
